import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Modal, StatusBadge } from '../ui';
import { useAuthStore, useMarketplaceStore } from '../../stores';
import { isContractVisibleToUser } from '../../utils/contractsVisibility';
import { resolveContractRouteSummary } from '../../utils/routeSummary';
import { CopyableGuideNumber } from '../ui/CopyableGuideNumber';

export function ActiveTrackingBadge() {
    const navigate = useNavigate();
    const location = useLocation();
    const user = useAuthStore((state) => state.user);
    const myContracts = useMarketplaceStore((state) => state.myContracts);
    const fetchMyContracts = useMarketplaceStore((state) => state.fetchMyContracts);
    const [isChooserOpen, setIsChooserOpen] = useState(false);

    const activeContracts = useMemo(
        () => myContracts
            .filter((contract) => contract.status === 'in_transit')
            .filter((contract) => isContractVisibleToUser(contract, user))
            .sort((left, right) => right.id - left.id),
        [myContracts, user],
    );

    useEffect(() => {
        if (!user || user.is_admin) {
            return;
        }

        if (myContracts.length === 0) {
            void fetchMyContracts();
        }

        const intervalId = window.setInterval(() => {
            void fetchMyContracts();
        }, 60000);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [fetchMyContracts, myContracts.length, user]);

    const currentTrackingMatch = location.pathname.match(/^\/marketplace\/contracts\/(\d+)\/tracking(?:[/?#].*)?$/);
    const currentTrackingContractId = currentTrackingMatch ? Number(currentTrackingMatch[1]) : null;

    if (!user || user.is_admin || activeContracts.length === 0) {
        return null;
    }

    if (activeContracts.length === 1 && currentTrackingContractId === activeContracts[0].id) {
        return null;
    }

    const handleNavigateToTracking = (contractId: number) => {
        setIsChooserOpen(false);
        navigate(`/marketplace/contracts/${contractId}/tracking`);
    };

    return (
        <>
            <button
                type="button"
                className="fixed left-1/2 z-[950] flex min-h-12 -translate-x-1/2 items-center gap-2 rounded-full border border-primary/20 bg-primary px-4 py-3 text-sm font-semibold text-white shadow-lg transition hover:bg-primary-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35"
                style={{
                    bottom: 'calc(var(--bottom-nav-height) + var(--spacing-safe-bottom, 0px) + 0.75rem)',
                }}
                onClick={() => {
                    if (activeContracts.length === 1) {
                        handleNavigateToTracking(activeContracts[0].id);
                        return;
                    }

                    setIsChooserOpen(true);
                }}
                aria-label={activeContracts.length === 1 ? 'Abrir tracking activo' : 'Elegir tracking activo'}
            >
                <span className="inline-flex size-6 items-center justify-center rounded-full bg-white/15">🚚</span>
                {activeContracts.length === 1 ? 'Hay tracking activo' : `${activeContracts.length} trackings activos`}
            </button>

            <Modal
                isOpen={isChooserOpen}
                onClose={() => setIsChooserOpen(false)}
                title="Trackings activos"
                size="medium"
            >
                <div className="space-y-3">
                    <p className="text-sm text-text-secondary">
                        Elige el contrato que quieres retomar sin volver a la lista completa.
                    </p>

                    <div className="space-y-2">
                        {activeContracts.map((contract) => {
                            const route = resolveContractRouteSummary(contract);

                            return (
                                <button
                                    key={contract.id}
                                    type="button"
                                    className="flex w-full items-center justify-between gap-3 rounded-2xl border border-border bg-background px-4 py-3 text-left transition hover:border-primary/25 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35"
                                    onClick={() => handleNavigateToTracking(contract.id)}
                                >
                                    <div className="min-w-0">
                                        <p className="text-sm font-semibold text-text tabular-nums" translate="no">
                                            <CopyableGuideNumber guideNumber={contract.guide_number ?? contract.request?.guide_number} fallbackId={contract.id} className="text-sm font-semibold text-text" />
                                        </p>
                                        <p className="mt-1 truncate text-xs text-text-muted">
                                            {route.origin} → {route.destination}
                                        </p>
                                    </div>
                                    <StatusBadge status="in_transit" label="En seguimiento" dot />
                                </button>
                            );
                        })}
                    </div>

                    <div className="flex justify-end pt-2">
                        <Button type="button" variant="secondary" onClick={() => setIsChooserOpen(false)}>
                            Cerrar
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    );
}
